'use client';

import Link from 'next/link';
import { useMemo } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useInstrumentCache } from '@/components/catalogo/ticker-cache-provider';
import type { InstrumentRow } from '@/components/catalogo/types';
import { buildComparatorPath } from '@/libs/comparador-route';
import { displayInstrument } from '@/libs/normalize-ticker';

type CompareWithDialogProps = {
  open: boolean;
  onClose: () => void;
  currentInstrument: string;
  selectedInstruments: string[];
  onSelectedInstrumentsChange: (instruments: string[]) => void;
  maxTotal?: number;
  showPrimaryAction?: boolean;
  primaryActionLabel?: string;
  primaryActionHref?: string;
};

const MAX_OPTIONS = 30;
const toInstrumentLabel = (ticker: Pick<InstrumentRow, 'idCem' | 'fundTypeSymbol'>) =>
  displayInstrument(ticker.idCem, ticker.fundTypeSymbol);

const normalizeInstrument = (value: string): string =>
  value
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]/g, '');

const toBaseIdCem = (ticker: string): string => {
  const normalized = normalizeInstrument(ticker);
  if (normalized.endsWith('11') && normalized.length >= 6) {
    return normalized.slice(0, -2);
  }
  return normalized;
};

const buildComparadorPathFromAssets = (items: string[]): string => {
  return buildComparatorPath(items);
};

export function CompareWithDialog({
  open,
  onClose,
  currentInstrument,
  selectedInstruments,
  onSelectedInstrumentsChange,
  maxTotal = 5,
  showPrimaryAction = true,
  primaryActionLabel = 'Abrir comparador',
  primaryActionHref,
}: CompareWithDialogProps) {
  const { tickers, loading } = useInstrumentCache();

  const availableOptions = useMemo(() => tickers.filter((ticker) => ticker.fundTypeSymbol !== 'TESOURO'), [tickers]);

  const selectedOptions = useMemo(() => {
    const selectedSet = new Set(selectedInstruments.map((item) => toBaseIdCem(item)));
    return availableOptions.filter((option) => selectedSet.has(option.idCem.toUpperCase()));
  }, [availableOptions, selectedInstruments]);

  const compareUrl = useMemo(() => {
    const ativos = Array.from(new Set(selectedInstruments.map((item) => normalizeInstrument(item))));
    return buildComparadorPathFromAssets(ativos);
  }, [selectedInstruments]);
  const resolvedPrimaryHref = primaryActionHref ?? compareUrl;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
      <DialogTitle>Comparar com</DialogTitle>
      <DialogContent>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1.25 }}>
          Ativo atual: <strong>{currentInstrument}</strong>. Selecione até {maxTotal} ativos no total.
        </Typography>

        <Autocomplete
          multiple
          options={availableOptions}
          loading={loading}
          value={selectedOptions}
          getOptionDisabled={(option) =>
            selectedOptions.length >= maxTotal && !selectedOptions.some((selected) => selected.idCem === option.idCem)
          }
          getOptionLabel={(option) => `${toInstrumentLabel(option)} - ${option.name}`}
          filterOptions={(items, state) => {
            const query = state.inputValue.trim().toLowerCase();
            const base = !query
              ? items
              : items.filter((option) => {
                  const label = toInstrumentLabel(option).toLowerCase();
                  const company = option.companyName?.toLowerCase() ?? '';
                  const fundType = option.fundTypeDescription?.toLowerCase() ?? '';
                  return (
                    label.includes(query) ||
                    option.name.toLowerCase().includes(query) ||
                    company.includes(query) ||
                    fundType.includes(query)
                  );
                });
            return base.slice(0, MAX_OPTIONS);
          }}
          onChange={(_, nextOptions) => {
            const nextInstruments = nextOptions.slice(0, maxTotal).map((item) => toInstrumentLabel(item));
            onSelectedInstrumentsChange(nextInstruments);
          }}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box component='li' key={key} {...optionProps} sx={{ py: 1 }}>
                <Box sx={{ minWidth: 72 }}>
                  <Typography fontWeight={700}>{toInstrumentLabel(option)}</Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {option.fundTypeSymbol}
                  </Typography>
                </Box>
                <Box sx={{ ml: 2, flex: 1 }}>
                  <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }}>
                    {option.name}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{
                      display: 'block',
                      fontSize: 11.5,
                      lineHeight: 1.3,
                      mt: 0.25,
                    }}
                  >
                    {option.companyName ?? ''}
                  </Typography>
                </Box>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size='small'
              placeholder='Buscar ticker para comparar'
              inputProps={{
                ...params.inputProps,
                suppressHydrationWarning: true,
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          PaperComponent={(props) => (
            <Paper
              {...props}
              elevation={6}
              sx={{
                borderRadius: 2,
                mt: 1,
                border: '1px solid hsl(var(--border))',
              }}
            />
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='outlined'>
          Fechar
        </Button>
        {showPrimaryAction ? (
          <Button
            component={Link}
            href={resolvedPrimaryHref}
            variant='contained'
            disabled={selectedInstruments.length === 0}
            onClick={onClose}
          >
            {primaryActionLabel}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
